/**
 * Wave 29 Chunk 9a — Team Competition full-page editor.
 *
 * Routes:
 *   /team/competitions/new            — create
 *   /team/competitions/:id/edit       — edit
 *
 * No modals — full page (DESIGN_CONTRACT Rule 0).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Trophy, ChevronLeft, Save, Loader2, Users as UsersIcon,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import DatePickerInput from "../components/DatePickerInput";
import VoiceTextarea from "../components/VoiceTextarea";

const EMPLOYMENT_TYPES = [
  { v: "auto", l: "Auto — track an existing performance metric" },
  { v: "manual", l: "Manual — HR scores each participant by hand" },
];

export default function TeamCompetitionEditor() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const isHr = user && ["owner", "admin"].includes(user.role);

  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [metrics, setMetrics] = useState([]);
  const [people, setPeople] = useState([]);

  const [form, setForm] = useState({
    name: "",
    description: "",
    metric_source: "auto",
    metric_key: "jcs_completed",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    participants: [],
    higher_is_better: true,
    prize: "",
    badge_label: "",
    badge_icon: "trophy",
    auto_grant_points: false,
    payout_first: 50,
    payout_second: 25,
    payout_third: 10,
  });

  // Bootstrap (metrics catalogue + people pool, plus existing record if edit).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const [meta, pool] = await Promise.all([
          api.get("/team/competitions"),
          api.get("/team/employees").catch(() => ({ data: { items: [] } })),
        ]);
        if (cancelled) return;
        setMetrics(meta?.data?.metrics || []);
        const items = pool?.data?.items || pool?.data?.employees || pool?.data || [];
        setPeople(items.map((u) => ({
          id: u.user_id || u.id,
          name: u.name || u.full_name || u.email,
          role: u.role,
        })).filter((u) => u.id));
        if (isEdit) {
          const { data } = await api.get(`/team/competitions/${id}`);
          setForm((f) => ({
            ...f,
            name: data?.name || "",
            description: data?.description || "",
            metric_source: data?.metric_source || "auto",
            metric_key: data?.metric_key || "jcs_completed",
            start_date: data?.start_date || f.start_date,
            end_date: data?.end_date || "",
            participants: data?.participants || [],
            higher_is_better: data?.higher_is_better ?? true,
            prize: data?.prize || "",
            badge_label: data?.badge_label || "",
            badge_icon: data?.badge_icon || "trophy",
            auto_grant_points: !!data?.auto_grant_points,
            payout_first: data?.payout_first ?? 50,
            payout_second: data?.payout_second ?? 25,
            payout_third: data?.payout_third ?? 10,
          }));
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't bootstrap form.");
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, id, isEdit]);

  const allParticipants = useMemo(() => people, [people]);
  const togglePerson = (uid) => {
    setForm((f) => {
      const sel = new Set(f.participants || []);
      if (sel.has(uid)) sel.delete(uid);
      else sel.add(uid);
      return { ...f, participants: Array.from(sel) };
    });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) return toast.error("Name is required.");
    if (!form.start_date || !form.end_date) return toast.error("Pick start + end dates.");
    if (form.end_date < form.start_date) return toast.error("End date must be after start.");
    if (form.metric_source === "auto" && !form.metric_key) return toast.error("Pick a metric.");
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        metric_source: form.metric_source,
        metric_key: form.metric_source === "auto" ? form.metric_key : null,
        start_date: form.start_date,
        end_date: form.end_date,
        participants: form.participants || [],
        higher_is_better: !!form.higher_is_better,
        prize: form.prize.trim() || null,
        badge_label: form.badge_label.trim() || null,
        badge_icon: form.badge_icon || "trophy",
        auto_grant_points: !!form.auto_grant_points,
        payout_first: Number(form.payout_first) || 0,
        payout_second: Number(form.payout_second) || 0,
        payout_third: Number(form.payout_third) || 0,
      };
      let savedId = id;
      if (isEdit) {
        await api.patch(`/team/competitions/${id}`, body);
      } else {
        const { data } = await api.post("/team/competitions", body);
        savedId = data?.id;
      }
      toast.success("Saved");
      nav(savedId ? `/team/competitions/${savedId}` : "/team/competitions");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (!isHr) {
    return (
      <div className="hv-card p-6 text-sm text-[#dde3ec]" data-testid="comp-edit-forbidden">
        Only owners / admins can manage competitions.
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl" data-testid="comp-editor-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/team/competitions" className="hv-btn hv-btn-ghost text-xs" data-testid="comp-edit-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-300" />
          {isEdit ? "Edit competition" : "New competition"}
        </h1>
        <span />
      </div>

      {busy ? (
        <div className="hv-card p-6 text-sm text-[#9aa3b5]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
      ) : (
        <form onSubmit={submit} className="hv-card p-4 sm:p-5 space-y-5" data-testid="comp-edit-form">
          <Field label="Name">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="hv-input text-sm w-full"
              data-testid="comp-name"
              placeholder="e.g. Most jobs completed — May 2026"
            />
          </Field>

          <Field label="Description (optional)">
            <VoiceTextarea
              rows={3}
              value={form.description}
              onChange={(v) => setForm({...form, description: v})}
              data-testid="comp-description"
              placeholder="What's being measured and why?"
             testIdPrefix="comp-description-voice" context="note" />
          </Field>

          <Field label="Metric source">
            <select
              value={form.metric_source}
              onChange={(e) => setForm({ ...form, metric_source: e.target.value })}
              className="hv-input text-sm w-full"
              data-testid="comp-metric-source"
            >
              {EMPLOYMENT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </Field>

          {form.metric_source === "auto" && (
            <Field label="Tracked metric">
              <select
                value={form.metric_key}
                onChange={(e) => setForm({ ...form, metric_key: e.target.value })}
                className="hv-input text-sm w-full"
                data-testid="comp-metric-key"
              >
                {metrics.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.label} — {m.description}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Start date">
              <DatePickerInput value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testId="comp-start" />
            </Field>
            <Field label="End date">
              <DatePickerInput value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testId="comp-end" />
            </Field>
          </div>

          <Field label="Prize (optional)">
            <input
              type="text"
              value={form.prize}
              onChange={(e) => setForm({ ...form, prize: e.target.value })}
              className="hv-input text-sm w-full"
              data-testid="comp-prize"
              placeholder="e.g. R 500 voucher · braai pack · paid day off"
            />
          </Field>

          <Field label="Badge label (optional)">
            <input
              type="text"
              value={form.badge_label}
              onChange={(e) => setForm({ ...form, badge_label: e.target.value })}
              className="hv-input text-sm w-full"
              data-testid="comp-badge-label"
              placeholder="e.g. 'May Workhorse'"
            />
          </Field>

          <div className="hv-card p-3 space-y-3 bg-white/[0.02] border border-white/5">
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
              <input
                type="checkbox"
                checked={form.auto_grant_points}
                onChange={(e) => setForm({ ...form, auto_grant_points: e.target.checked })}
                data-testid="comp-auto-grant"
              />
              Auto-grant points to top 3 when this competition closes
            </label>
            {form.auto_grant_points && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="1st place">
                  <input type="number" min={0} value={form.payout_first}
                    onChange={(e) => setForm({ ...form, payout_first: e.target.value })}
                    className="hv-input text-sm w-full" data-testid="comp-payout-1" />
                </Field>
                <Field label="2nd place">
                  <input type="number" min={0} value={form.payout_second}
                    onChange={(e) => setForm({ ...form, payout_second: e.target.value })}
                    className="hv-input text-sm w-full" data-testid="comp-payout-2" />
                </Field>
                <Field label="3rd place">
                  <input type="number" min={0} value={form.payout_third}
                    onChange={(e) => setForm({ ...form, payout_third: e.target.value })}
                    className="hv-input text-sm w-full" data-testid="comp-payout-3" />
                </Field>
              </div>
            )}
          </div>

          <Field label={(
            <span className="flex items-center gap-1"><UsersIcon className="w-3.5 h-3.5" />Participants ({form.participants.length || allParticipants.length})</span>
          )}>
            <p className="text-[11px] text-[#9aa3b5] mb-2">Leave empty to include all active staff.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-72 overflow-auto" data-testid="comp-participants">
              {allParticipants.map((p) => {
                const checked = form.participants.includes(p.id);
                return (
                  <label key={p.id} className="flex items-center gap-2 text-xs text-[#dde3ec] cursor-pointer bg-white/[0.02] hover:bg-white/[0.05] rounded px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePerson(p.id)}
                      data-testid={`comp-participant-${p.id}`}
                    />
                    <span className="truncate">{p.name}</span>
                    <span className="text-[10px] text-[#647087] ml-auto">{p.role}</span>
                  </label>
                );
              })}
              {allParticipants.length === 0 && (
                <div className="text-xs text-[#9aa3b5] col-span-2">No employees to choose from.</div>
              )}
            </div>
          </Field>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
            <Link to="/team/competitions" className="hv-btn hv-btn-ghost text-xs">Cancel</Link>
            <button type="submit" disabled={saving} className="hv-btn hv-btn-primary text-xs disabled:opacity-50" data-testid="comp-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {isEdit ? "Save changes" : "Create competition"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">{label}</div>
      {children}
    </label>
  );
}
